import { useEffect, useRef, useState, useCallback } from 'react';
import { facebookPosts } from '../../json/facebookPosts';

const ITEM_WIDTH = 296;
const VISIBLE_COUNT = 4;
const MAX_RETRIES = 15;

const FacebookPostsCarousel = () => {
  const scrollRef = useRef(null);
  const postsContainerRef = useRef(null);
  const intervalRef = useRef(null);
  const isTransitioning = useRef(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const extendedPosts = [...facebookPosts, ...facebookPosts.slice(0, VISIBLE_COUNT)];
  const originalLength = facebookPosts.length;

  // Cargar SDK de Facebook de forma robusta
  useEffect(() => {
    // Asegurar que existe fb-root
    if (!document.getElementById('fb-root')) {
      const fbRoot = document.createElement('div');
      fbRoot.id = 'fb-root';
      document.body.prepend(fbRoot);
    }

    // Si ya está cargado
    if (window.FB) {
      setSdkReady(true);
      return;
    }

    // Definir callback global antes de cargar el script
    window.fbAsyncInit = function() {
      window.FB.init({ xfbml: true, version: 'v19.0' });
      setSdkReady(true);
    };

    // Cargar script solo si no existe
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/es_LA/sdk.js#xfbml=1&version=v19.0';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      
      script.onerror = () => {
        console.error('Error al cargar SDK de Facebook');
        setLoadError(true);
      };
      
      document.body.appendChild(script);
    }

    // Fallback: si después de 10 segundos no cargó, mostrar error
    const timeout = setTimeout(() => {
      if (!window.FB) setLoadError(true);
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  // Parsear posts cuando el SDK esté listo
  useEffect(() => {
    if (!sdkReady || !postsContainerRef.current) return;

    let retries = 0;
    const tryParse = () => {
      if (window.FB && window.FB.XFBML && postsContainerRef.current) {
        try {
          window.FB.XFBML.parse(postsContainerRef.current);
        } catch (e) {
          console.warn('Error parseando posts:', e);
        }
      } else if (retries < MAX_RETRIES) {
        retries++;
        setTimeout(tryParse, 800);
      }
    };

    // Esperar un poco para que Facebook procese el DOM
    const timer = setTimeout(tryParse, 1000);
    return () => clearTimeout(timer);
  }, [sdkReady]);

  const checkLoop = useCallback(() => {
    if (!scrollRef.current || isTransitioning.current) return;
    const maxRealScroll = originalLength * ITEM_WIDTH;

    if (scrollRef.current.scrollLeft >= maxRealScroll - 5) {
      isTransitioning.current = true;
      scrollRef.current.style.scrollBehavior = 'auto';
      scrollRef.current.scrollLeft = scrollRef.current.scrollLeft - maxRealScroll;
      scrollRef.current.style.scrollBehavior = 'smooth';
      isTransitioning.current = false;
    }
  }, [originalLength]);

  const scrollOne = useCallback((direction) => {
    if (!scrollRef.current || isTransitioning.current) return;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -ITEM_WIDTH : ITEM_WIDTH,
      behavior: 'smooth'
    });
    setTimeout(() => checkLoop(), 350);
  }, [checkLoop]);

  const startAutoScroll = useCallback(() => {
    intervalRef.current = setInterval(() => {
      scrollOne('right');
    }, 4000);
  }, [scrollOne]);

  useEffect(() => {
    if (!sdkReady) return;
    startAutoScroll();
    return () => clearInterval(intervalRef.current);
  }, [sdkReady, startAutoScroll]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => { if (!isTransitioning.current) checkLoop(); };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [checkLoop]);

  const handleMouseEnter = () => clearInterval(intervalRef.current);
  const handleMouseLeave = () => startAutoScroll();

  // Fallback si Facebook no carga
  if (loadError) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          No se pudieron cargar las publicaciones de Facebook.
        </p>
        <a 
          href="https://www.facebook.com/Hidalgo.Bienestar" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            backgroundColor: '#6b0f1a',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold'
          }}
        >
          Ver publicaciones en Facebook
        </a>
      </div>
    );
  }

  return (
    <div 
      style={{ position: 'relative', width: '100%', padding: '0 10px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Flechas */}
      <button onClick={() => scrollOne('left')} aria-label="Anterior" style={{
        position: 'absolute', left: '-10px', top: '50%', transform: 'translateY(-50%)',
        zIndex: 10, background: '#6b0f1a', color: '#fff', border: 'none',
        borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer',
        fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)'
      }}>‹</button>

      <button onClick={() => scrollOne('right')} aria-label="Siguiente" style={{
        position: 'absolute', right: '-10px', top: '50%', transform: 'translateY(-50%)',
        zIndex: 10, background: '#6b0f1a', color: '#fff', border: 'none',
        borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer',
        fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)'
      }}>›</button>

      {/* Carrusel */}
      <div ref={scrollRef} style={{
        display: 'flex', gap: '16px', overflowX: 'auto', scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth', padding: '10px 4px', scrollbarWidth: 'none', alignItems: 'stretch'
      }}>
        <style>{`div::-webkit-scrollbar { display: none; }`}</style>
        
        <div ref={postsContainerRef} style={{ display: 'contents' }}>
          {extendedPosts.map((url, index) => (
            <div key={`${url}-${index}`} style={{
              flex: '0 0 280px', width: '280px', minWidth: '280px', height: '400px',
              scrollSnapAlign: 'start', borderRadius: '8px', overflow: 'hidden',
              backgroundColor: '#fff', border: '1px solid #e0e0e0'
            }}>
              <div className="fb-post" data-href={url} data-width="280" data-show-text="true"
                style={{ width: '100%', height: '100%' }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FacebookPostsCarousel;